import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Card from '../common/Card';
import Input from '../common/Input';
import Button from '../common/Button';
import Alert from '../common/Alert';
import api from '../../services/api';
import { validateSKU, validateBarcode, validatePositiveNumber } from '../../utils/validators';
import '../../styles/product-form.css';

const ProductForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [formData, setFormData] = useState({
    product_name: '',
    sku: '',
    barcode: '',
    category_id: '',
    description: '',
    cost_price: '',
    selling_price: '',
    wholesale_price: '',
    tax_rate: 0,
    unit: 'piece',
    min_stock_level: 5,
    max_stock_level: '',
    current_stock: 0,
    image_url: ''
  });

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [alert, setAlert] = useState(null);

  useEffect(() => {
    loadCategories();
    if (isEditMode) {
      loadProduct();
    }
  }, [id]);

  const loadCategories = async () => {
    try {
      const response = await api.get('/categories.php');
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const loadProduct = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/products.php/${id}`);
      setFormData(response.data);
    } catch (error) {
      console.error('Failed to load product:', error);
      setAlert({
        type: 'error',
        message: 'Failed to load product details'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.product_name.trim()) {
      newErrors.product_name = 'Product name is required';
    }

    if (!formData.selling_price) {
      newErrors.selling_price = 'Selling price is required';
    } else if (!validatePositiveNumber(formData.selling_price)) {
      newErrors.selling_price = 'Invalid price';
    }

    if (formData.cost_price && !validatePositiveNumber(formData.cost_price)) {
      newErrors.cost_price = 'Invalid cost price';
    }

    if (formData.sku && !validateSKU(formData.sku)) {
      newErrors.sku = 'Invalid SKU format';
    }

    if (formData.barcode && !validateBarcode(formData.barcode)) {
      newErrors.barcode = 'Invalid barcode format';
    }

    if (formData.min_stock_level && !validatePositiveNumber(formData.min_stock_level)) {
      newErrors.min_stock_level = 'Invalid minimum stock level';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      if (isEditMode) {
        await api.put(`/products.php/${id}`, formData);
        setAlert({
          type: 'success',
          message: 'Product updated successfully'
        });
      } else {
        const response = await api.post('/products.php', formData);
        setAlert({
          type: 'success',
          message: 'Product created successfully'
        });
        setTimeout(() => {
          navigate(`/products/${response.data.product_id}`);
        }, 1500);
      }
    } catch (error) {
      console.error('Failed to save product:', error);
      setAlert({
        type: 'error',
        message: error.response?.data?.error || 'Failed to save product'
      });
    } finally {
      setLoading(false);
    }
  };

  const units = [
    'piece', 'kg', 'g', 'liter', 'ml', 'box', 'pack', 'dozen', 'meter'
  ];

  if (loading && isEditMode) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="product-form">
      <Card>
        <div className="form-header">
          <h2>{isEditMode ? 'Edit Product' : 'Add New Product'}</h2>
          <div style={{ display: 'flex', gap: '10px' }}>
            {isEditMode && (
              <Button 
                variant="primary" 
                onClick={() => navigate(`/products/stock/${id}`)}
                icon="📦"
                type="button"
              >
                Adjust Stock / Restock
              </Button>
            )}
            <Button variant="secondary" onClick={() => navigate('/products')} type="button">
              Cancel
            </Button>
          </div>
        </div>

        {alert && (
          <Alert
            type={alert.type}
            message={alert.message}
            closable
            onClose={() => setAlert(null)}
          />
        )}

        <form onSubmit={handleSubmit} className="form">
          <div className="form-row">
            <div className="form-col-8">
              <Input
                label="Product Name"
                name="product_name"
                value={formData.product_name}
                onChange={handleChange}
                error={errors.product_name}
                required
              />
            </div>
            <div className="form-col-4">
              <Input
                label="Category"
                name="category_id"
                type="select"
                value={formData.category_id}
                onChange={handleChange}
              >
                <option value="">Select Category</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.category_name}</option>
                ))}
              </Input>
            </div>
          </div>

          <div className="form-row">
            <div className="form-col-4">
              <Input
                label="SKU"
                name="sku"
                value={formData.sku}
                onChange={handleChange}
                error={errors.sku}
                hint="Unique product identifier"
              />
            </div>
            <div className="form-col-4">
              <Input
                label="Barcode"
                name="barcode"
                value={formData.barcode}
                onChange={handleChange}
                error={errors.barcode}
                hint="EAN-13 or UPC-A"
              />
            </div>
            <div className="form-col-4">
              <Input
                label="Unit"
                name="unit"
                type="select"
                value={formData.unit}
                onChange={handleChange}
              >
                {units.map(unit => (
                  <option key={unit} value={unit}>{unit}</option>
                ))}
              </Input>
            </div>
          </div>

          <div className="form-row">
            <div className="form-col-4">
              <Input
                label="Cost Price"
                name="cost_price"
                type="number"
                value={formData.cost_price}
                onChange={handleChange}
                error={errors.cost_price}
                prefix="KSh"
              />
            </div>
            <div className="form-col-4">
              <Input
                label="Selling Price"
                name="selling_price"
                type="number"
                value={formData.selling_price}
                onChange={handleChange}
                error={errors.selling_price}
                prefix="KSh"
                required
              />
            </div>
            <div className="form-col-4">
              <Input
                label="Wholesale Price"
                name="wholesale_price"
                type="number"
                value={formData.wholesale_price}
                onChange={handleChange}
                prefix="KSh"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-col-4">
              <Input
                label="Tax Rate (%)"
                name="tax_rate"
                type="number"
                value={formData.tax_rate}
                onChange={handleChange}
                min="0"
                max="100"
              />
            </div>
            <div className="form-col-4">
              <Input
                label="Min Stock Level"
                name="min_stock_level"
                type="number"
                value={formData.min_stock_level}
                onChange={handleChange}
                error={errors.min_stock_level}
              />
            </div>
            <div className="form-col-4">
              <Input
                label="Max Stock Level"
                name="max_stock_level"
                type="number"
                value={formData.max_stock_level}
                onChange={handleChange}
              />
            </div>
          </div>

          {!isEditMode && (
            <div className="form-row">
              <div className="form-col-4">
                <Input
                  label="Initial Stock"
                  name="current_stock"
                  type="number"
                  value={formData.current_stock}
                  onChange={handleChange}
                />
              </div>
            </div>
          )}

          <div className="form-row">
            <div className="form-col-12">
              <Input
                label="Description"
                name="description"
                type="textarea"
                value={formData.description}
                onChange={handleChange}
                rows="4"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-col-12">
              <Input
                label="Image URL"
                name="image_url"
                value={formData.image_url}
                onChange={handleChange}
                placeholder="https://example.com/image.jpg"
              />
            </div>
          </div>

          <div className="form-actions">
            <Button type="submit" variant="primary" loading={loading}>
              {isEditMode ? 'Update Product' : 'Create Product'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default ProductForm;